import React from 'react';

import useVisitStore from '../../stores/visitStore';
import { FollowUpDialog } from '../../components/dashboard/WorkflowShared';

/**
 * FollowUpWidget — schedules a follow-up for a visit. Wraps the
 * existing FollowUpDialog (date presets, report-needed checkbox) so the
 * UX is byte-identical to the legacy in-page dialog while routing
 * execution through the widget SDK. The dialog component itself stays
 * in `WorkflowShared.jsx` for now; folding the form body into this
 * file is a follow-up refactor.
 *
 * Context:
 *   visitId — required
 *   prefill?.name — display name shown in the dialog ("schedule follow-up for X")
 *
 * Side channel: dispatches `arogya:widgets:follow-up-saved` after a
 * successful update so other surfaces (e.g. ManageVisitPage's list view)
 * can refetch / clear local state if they want.
 */
export default function FollowUpWidget({ context, onClose }) {
  const updateVisit = useVisitStore((s) => s.updateVisit);
  const visitId = context?.visitId;
  const name = (context?.prefill && context.prefill.name) || 'this patient';

  // Match the host page's dark-mode toggle — we read from localStorage
  // since the widget doesn't have ThemeContext threaded through. The
  // dialog renders correctly in either mode.
  const isDark = typeof document !== 'undefined'
    && document.documentElement.classList.contains('dark');

  const handleConfirm = async (meta) => {
    if (!visitId) { onClose?.(); return; }
    try {
      await updateVisit(visitId, {
        report_needed: meta.reportNeeded,
        follow_up_required: meta.followUpRequired,
        follow_up_date: meta.followUpDate || null,
      });
      try {
        window.dispatchEvent(new CustomEvent('arogya:widgets:follow-up-saved', {
          detail: { visitId, meta },
        }));
      } catch (_e) { /* ignore */ }
    } finally {
      onClose?.();
    }
  };

  return (
    <FollowUpDialog
      isDark={isDark}
      name={name}
      onConfirm={handleConfirm}
      onCancel={() => onClose?.()}
    />
  );
}
